// 批量引擎(通用)。工具传入:category / client / tier / token / userId / clientConvert / buildQuery / onSignup / onUpgrade。
// 客户端类 → 浏览器循环跑 clientConvert;后端类 → 逐个 /batch/upload + /batch/start + 轮询 /job + 取 /result。
// gating 是前端 UX(按档锁超额文件 + 弹墙);真正强制在后端 /batch。
// 下载(单个 + zip)走 app 的 /download 页(同源新标签读 blob: URL —— 本标签开着 blob 就有效;zip 由 /download 用 JSZip 打包)。
import { ref, computed, unref } from "vue";
import { batchLimit, perFileMax } from "@/settings/free-tools/batch-limits.js";
import { useFreeToolsEvent } from "@/composables/useFreeToolsEvent";

let _uid = 0;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const randId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "b" + Date.now() + Math.random().toString(36).slice(2);

export function useBatch(opts) {
  const items = ref([]); // {id,file,name,size,srcExt,status,prog,outBlob,outUrl,outExt,outSize,direct,error,tooLarge,lockReason,_idx}
  const busy = ref(false);
  // 后端产物统一走 R2 公开域名直连下载(对象自带 Content-Disposition,任意大小、不经 Nitro 代理);打包走 worker 流式 /zip。(nuxt#35)
  const fastapiBase = (useRuntimeConfig().public.fastapiUrl || "").replace(/\/+$/, "");
  const userR2Base = (useRuntimeConfig().public.userR2Url || "").replace(/\/+$/, "");
  const curBatchId = ref(""); // 最近一次后端批量 id(供打包 /zip 用)

  // 观测埋点:批量转换也进 Analytics Engine(mode=batch-backend/batch-client,与单文件区分)。(nuxt#37)
  const { reportEvent } = useFreeToolsEvent();
  const toolSlug = () =>
    unref(opts.slug) ||
    (typeof window !== "undefined" ? (window.location.pathname || "").split("/").filter(Boolean).pop() : "") ||
    "";
  const targetFmt = () => {
    try { return new URLSearchParams((opts.buildQuery && opts.buildQuery()) || "").get("to_format") || ""; }
    catch (_) { return ""; }
  };

  const limit = computed(() => batchLimit(unref(opts.category), unref(opts.tier), unref(opts.client)));
  const maxBytes = computed(() => perFileMax(unref(opts.category)));

  const validItems = computed(() => items.value.filter((i) => !i.tooLarge)); // 未超大(可能被锁)
  const activeItems = computed(() => items.value.filter((i) => !i.tooLarge && !i.lockReason));
  const lockedItems = computed(() => items.value.filter((i) => i.lockReason));
  const oversizeItems = computed(() => items.value.filter((i) => i.tooLarge));
  const total = computed(() => activeItems.value.length);
  const doneCount = computed(() => activeItems.value.filter((i) => i.status === "done").length);
  const failedCount = computed(() => activeItems.value.filter((i) => i.status === "failed").length);
  const allDone = computed(() => total.value > 0 && doneCount.value + failedCount.value >= total.value);
  const gateKind = computed(() => {
    if (!lockedItems.value.length) return "";
    return unref(opts.tier) === "anon" ? "signup" : "upgrade";
  });

  // 按「未超大文件的位置」重算锁定:前 limit 个放行,其余锁(anon→注册墙 / 其它→升级墙)。
  function relock() {
    const lim = limit.value;
    const kind = unref(opts.tier) === "anon" ? "signup" : "upgrade";
    let validIdx = 0;
    for (const it of items.value) {
      if (it.tooLarge) { it.lockReason = null; continue; }
      it.lockReason = validIdx < lim ? null : kind;
      validIdx++;
    }
  }

  function addFiles(fileList) {
    const max = maxBytes.value;
    for (const file of Array.from(fileList || [])) {
      const tooLarge = file.size > max;
      items.value.push({
        id: ++_uid, file, name: file.name, size: file.size,
        srcExt: (file.name.split(".").pop() || "").toLowerCase(),
        status: "queued", prog: 0, tooLarge, lockReason: null,
        error: tooLarge ? `Too large — exceeds ${Math.round(max / 1048576)} MB` : "",
        outBlob: null, outUrl: "", outExt: "", outSize: 0, direct: false,
      });
    }
    relock();
  }

  function removeItem(id) {
    const i = items.value.findIndex((x) => x.id === id);
    if (i < 0) return;
    if (items.value[i].outUrl && items.value[i].outUrl.startsWith("blob:")) URL.revokeObjectURL(items.value[i].outUrl);
    items.value.splice(i, 1);
    relock();
  }
  function clearAll() {
    items.value.forEach((it) => it.outUrl && it.outUrl.startsWith("blob:") && URL.revokeObjectURL(it.outUrl));
    items.value = [];
  }
  // 套餐变化(登录/升级后)重新评估锁定 —— 工具在 tier 变化时调一次。
  function reevaluate() { relock(); }
  // 改了目标格式 / 质量等参数后:把已完成/失败项重新入队、清掉旧产物,
  // 让 allDone 回到 false、主按钮从「Download all」复位成「Convert all」,用新参数重转。
  function resetResults() {
    for (const it of items.value) {
      if (it.outUrl && it.outUrl.startsWith("blob:")) URL.revokeObjectURL(it.outUrl);
      it.outBlob = null; it.outUrl = ""; it.outExt = ""; it.outSize = 0; it.direct = false;
      if (!it.tooLarge && (it.status === "done" || it.status === "failed")) {
        it.status = "queued"; it.prog = 0; it.error = "";
      }
    }
  }

  async function start() {
    if (busy.value) return;
    const todo = activeItems.value.filter((i) => i.status === "queued" || i.status === "failed");
    if (!todo.length) return;
    todo.forEach((it) => { it.status = "queued"; it.prog = 0; it.error = ""; });
    busy.value = true;
    try {
      if (unref(opts.client)) await runClient(todo);
      else await runBackend(todo);
    } finally {
      busy.value = false;
    }
  }
  const retryFailed = () => start();

  async function runClient(todo) {
    const conc = 4;
    let next = 0;
    const worker = async () => {
      while (next < todo.length) {
        const it = todo[next++];
        it.status = "processing"; it.prog = 40;
        try {
          const r = await opts.clientConvert(it.file, it);
          it.outBlob = r.blob; it.outExt = r.ext || it.srcExt;
          if (it.outUrl) URL.revokeObjectURL(it.outUrl);
          it.outUrl = URL.createObjectURL(r.blob); it.outSize = r.blob.size;
          it.prog = 100; it.status = "done";
          reportEvent({ mode: "batch-client", slug: toolSlug(), src: it.srcExt || "", tgt: it.outExt || targetFmt(), status: "ok", inputBytes: (it.file && it.file.size) || it.size || 0, outputBytes: it.outSize || 0 });
        } catch (e) {
          it.status = "failed"; it.error = String((e && e.message) || e) || "Conversion failed";
          reportEvent({ mode: "batch-client", slug: toolSlug(), src: it.srcExt || "", tgt: targetFmt(), status: "error", error: it.error, inputBytes: (it.file && it.file.size) || it.size || 0 });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(conc, todo.length) }, worker));
  }

  async function runBackend(todo) {
    const batchId = randId();
    curBatchId.value = batchId;
    todo.forEach((it, i) => { it._idx = i; });
    // 逐个上传(避开 Worker 内存上限)
    for (const it of todo) {
      it.status = "processing"; it.prog = 8;
      try {
        const r = await fetch(`/api/free-tools/batch/upload?batchId=${batchId}&idx=${it._idx}&name=${encodeURIComponent(it.name || "")}`, { method: "POST", body: it.file });
        if (!r.ok) { it.status = "failed"; it.error = "Upload failed"; }
      } catch { it.status = "failed"; it.error = "Upload failed"; }
    }
    // 启动(服务端验档卡额度)
    let startRes;
    try {
      startRes = await fetch("/api/free-tools/batch/start", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batchId, count: todo.length, token: unref(opts.token) || "", userId: String(unref(opts.userId) || ""), query: opts.buildQuery() }),
      });
    } catch { todo.forEach((it) => { it.status = "failed"; it.error = "Start failed"; }); return; }
    if (startRes.status === 402) {
      const j = await startRes.json().catch(() => ({}));
      todo.forEach((it) => { it.status = "queued"; it.prog = 0; });
      if (j.reason === "paid_only" || j.tier === "free") opts.onUpgrade && opts.onUpgrade();
      else opts.onSignup && opts.onSignup();
      return;
    }
    if (startRes.status === 429) { todo.forEach((it) => { it.status = "failed"; it.error = "Too many requests — please slow down"; }); return; }
    if (!startRes.ok) { todo.forEach((it) => { it.status = "failed"; it.error = "Start failed"; }); return; }
    await pollBackend(batchId, todo);
  }

  async function pollBackend(batchId, todo) {
    const byIdx = new Map(todo.map((it) => [it._idx, it]));
    const reported = new Set(); // 每文件只上报一次(轮询会重复看到 done/error 项)
    for (let n = 0; n < 800; n++) {
      await sleep(1500);
      let j;
      try { j = await fetch(`/api/free-tools/job/${batchId}`, { cache: "no-store" }).then((r) => r.json()); }
      catch { continue; }
      if (!j || !Array.isArray(j.files)) {
        if (j && j.status === "error") { todo.forEach((it) => { if (it.status !== "done") { it.status = "failed"; it.error = j.detail || "failed"; } }); break; }
        continue;
      }
      for (const f of j.files) {
        const it = byIdx.get(f.idx);
        if (!it || it.status === "done") continue;
        if (f.status === "processing") { it.status = "processing"; if (it.prog < 60) it.prog = 60; }
        else if (f.status === "queued") { it.status = "queued"; }
        else if (f.status === "error") {
          it.status = "failed"; it.error = f.error || "Conversion failed";
          if (!reported.has(f.idx)) {
            reported.add(f.idx);
            reportEvent({ mode: "batch-backend", slug: toolSlug(), src: it.srcExt || "", tgt: targetFmt(), status: "error", error: it.error, inputBytes: it.size || (it.file && it.file.size) || 0 });
          }
        }
        else if (f.status === "done") {
          it.outExt = f.ext || ""; it.outSize = f.size || 0; it.prog = 100; it.status = "done";
          // 后端产物统一:R2 公开域名直连 URL(对象自带 Content-Disposition 原名 → 单文件经 /download 导航下载;
          // 打包走 worker 流式 /zip)。不取 blob、不经 Nitro 代理、任意大小。(nuxt#35)
          it.direct = true;
          if (it.outUrl && it.outUrl.startsWith("blob:")) URL.revokeObjectURL(it.outUrl);
          it.outUrl = userR2Base + "/free-tools/results/" + batchId + "-" + f.idx;
          if (!reported.has(f.idx)) {
            reported.add(f.idx);
            reportEvent({ mode: "batch-backend", slug: toolSlug(), src: it.srcExt || "", tgt: it.outExt || targetFmt(), status: "ok", inputBytes: it.size || (it.file && it.file.size) || 0, outputBytes: it.outSize || 0 });
          }
        }
      }
      if (j.status === "done" || (j.total > 0 && j.done >= j.total)) break;
    }
  }

  const outName = (it) => it.name.replace(/\.[^.]+$/, "") + "." + (it.outExt || it.srcExt);
  // 所有批量下载都经 app 的 /download/ 页面(承载广告 + Keep-creating 引流)。三条源:
  //   · 客户端产物(blob):img_path=逗号分隔 blob: URL,/download 用 JSZip 打包(小图,内存无压力)。
  //   · 后端单文件:direct=1 + img_path=R2 公开 URL → /download 导航下载(对象自带附件头,任意大小)。
  //   · 后端多文件:direct=1 + zip_url=worker 流式 /zip → /download 导航下载(任意大小,不进浏览器内存)。
  function openDownloadTab(params) {
    if (typeof window === "undefined") return;
    const qs = new URLSearchParams(params);
    qs.set("prev_route1", window.location.pathname);
    qs.set("prev_route1_title", (document.title || "Free Tools").split(/[—|]/)[0].trim());
    window.open("/download/?" + qs.toString(), "_blank");
  }
  // 客户端 blob(单/多):沿用 JSZip 路径。
  function openDownloadPage(urls, names, title) {
    openDownloadTab({ img_title: title, img_path: urls.join(","), img_names: names.join(",") });
  }
  // 后端单文件:R2 公开 URL 导航下载。
  function openDownloadDirect(url, name) {
    openDownloadTab({ img_title: name, img_path: url, direct: "1" });
  }
  // 后端多文件:worker 流式 zip;仍传 img_path=R2 URL 列表让卡片显示 "N files",实际下载走 zip_url。
  function openDownloadZip(urls, count) {
    const zipUrl = fastapiBase + "/zip?batchId=" + encodeURIComponent(curBatchId.value) + "&name=" + encodeURIComponent("sharkfoto-batch.zip");
    openDownloadTab({ img_title: count + " files", img_path: urls.join(","), zip_url: zipUrl, direct: "1" });
  }
  function downloadAll() {
    const done = activeItems.value.filter((i) => i.status === "done" && i.outUrl);
    if (!done.length) return;
    const backend = done.filter((i) => i.direct);    // 后端产物:R2 公开 URL
    const blobs = done.filter((i) => !i.direct);     // 客户端产物:blob
    if (backend.length === 1) openDownloadDirect(backend[0].outUrl, outName(backend[0]));
    else if (backend.length > 1) openDownloadZip(backend.map((i) => i.outUrl), backend.length); // 一个 worker 流式 zip,任意大小
    if (blobs.length === 1) openDownloadPage([blobs[0].outUrl], [outName(blobs[0])], outName(blobs[0]));
    else if (blobs.length > 1) openDownloadPage(blobs.map((i) => i.outUrl), blobs.map(outName), "sharkfoto-batch");
  }
  function downloadOne(it) {
    if (!it.outUrl) return;
    if (it.direct) return openDownloadDirect(it.outUrl, outName(it)); // 后端:R2 公开 URL 经 /download 导航
    openDownloadPage([it.outUrl], [outName(it)], outName(it));        // 客户端:blob 经 /download JSZip
  }

  function gateClick() {
    if (unref(opts.tier) === "anon") opts.onSignup && opts.onSignup();
    else opts.onUpgrade && opts.onUpgrade();
  }

  return {
    items, busy, limit, maxBytes,
    validItems, activeItems, lockedItems, oversizeItems, total, doneCount, failedCount, allDone, gateKind,
    addFiles, removeItem, clearAll, reevaluate, resetResults, start, retryFailed, downloadAll, downloadOne, gateClick,
  };
}
